/**
 * Behaviors Panel
 * Unity Inspector-style UI for viewing and editing behaviors attached to SceneObjects
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import type {
    ServiceDefinition,
    IShellService,
    ISettingsStore,
    SettingDescriptor,
    SidePaneDefinition
} from '@babylonjs/inspector';
import { ShellServiceIdentity, SettingsStoreIdentity } from '@babylonjs/inspector';
import type { TraceViewerElement } from 'trace-viewer';
import { documentRoot } from 'trace-viewer/editor';
import type { TraceBehavior, BehaviorData, FieldSchema, FieldValueMap } from 'trace-viewer/behaviors';
import type { AnimationData, AnimationSequenceData, RendererData, KeyframesData, PagesData, ParticleSystemData, PropertiesData, SynthData, TimelineData, BehaviorGraphData } from 'trace-viewer/behaviors';
import {
    Animation,
    AnimationSequence,
    Keyframes,
    Pages,
    Properties,
    Renderer,
    ParticleSystem,
    Synth,
    Timeline,
    BehaviorGraph,
    BoxCollider,
    SphereCollider,
    BEHAVIOR_SCHEMAS as IMPORTED_SCHEMAS,
    ScriptBehavior,
} from 'trace-viewer/behaviors';
import type { SceneObject, SceneData } from 'trace-viewer/scene';
import type { Vec3, Vec4 } from 'trace-viewer/types';
import type { Node as BabylonNode } from '@babylonjs/core/node';
import { EDITOR_EVENTS, dispatchBehaviorChanged } from './editor-events';
import type { BehaviorChangedDetail, KeyframeSelectionChangedDetail } from './editor-events';

/** Extract the correctly-typed value for a field from a data record. The single cast
 *  is here — call sites inside switch(field.type) get the narrowed return type for free. */
function fieldValue<F extends FieldSchema>(data: Record<string, unknown>, field: F): FieldValueMap[F['type']] {
    return data[field.key] as FieldValueMap[F['type']];
}
import {
    getUndoManager,
    BehaviorCommitCommand,
    AddBehaviorCommand,
    RemoveBehaviorCommand,
    RenameCommand,
} from 'trace-viewer/editor';
import { PropertiesEditor } from './behaviors/properties';
import { RendererEditor } from './behaviors/renderer';
import { BehaviorGraphEditor as BehaviorGraphEditorComponent } from './behaviors/behaviorgraph';
import { AnimationEditor } from './behaviors/animation';
import { AnimationSequenceEditor } from './behaviors/animationsequence';
import { TransformSection } from './behaviors/transform';
import { ParticleSystemEditor } from './behaviors/particles';
import { KeyframesEditor } from './behaviors/keyframes-editor';
import { capturePropertyEditIntoKeyframe, overlayKeyframeValues } from './keyframe-capture';
import { routePropertyEditIntoState, reconcilePropertyEditStates, overlayStateValues, toggleActive } from './state-capture';
import { PagesEditor } from './behaviors/pages';
import { SynthEditor } from './behaviors/synth';
import { TimelineEditor } from './behaviors/timeline';
import { JsonEditor } from './components/json-editor';
import { AddBehaviorButton } from './components/add-behavior-button';
import { AssetField } from './components/asset-field';
import { ScriptEditorSection } from './behaviors/script-editor-section';

import { getSelectionService } from './selection-bridge-service';
import { copyForInsideEdit, needsReferenceCopy } from './cms/reference-edit-guard';

// ============================================================================
// Behavior Schema (from local schemas folder)
// ============================================================================

// Convert to Record<string, FieldSchema[]> for field rendering
const BEHAVIOR_SCHEMAS: Record<string, FieldSchema[]> = Object.fromEntries(
    Object.entries(IMPORTED_SCHEMAS).map(([type, schema]) => [type, schema.fields])
);

// Get behavior-level description for tooltips
const getBehaviorDescription = (type: string): string | undefined => {
    return IMPORTED_SCHEMAS[type]?.description;
};


// ============================================================================
// Script Template
// ============================================================================

const SCRIPT_TEMPLATE = `import type { ScriptContext, BehaviorData } from 'trace:api';

export interface Data extends BehaviorData {
  '@type': '\${typeName}';

  /**
   * @default 5
   * @step 1
   */
  speed: number;

  /**
   * @default 0.5
   * @step 0.1
   */
  amplitude: number;
}

let baseY = 0;
let phase = 0;

export function onStart(ctx: ScriptContext<Data>) {
  baseY = ctx.self.position.y;
  ctx.log.info('Started!');
}

export function onUpdate(ctx: ScriptContext<Data>) {
  const { speed, amplitude } = ctx.data;
  phase += ctx.time.deltaTime * speed;
  const offset = Math.sin(phase) * amplitude;
  const pos = ctx.self.position;
  pos.y = baseY + offset;
  ctx.self.position = pos;
}
`;

// ============================================================================
// Shared Styles
// ============================================================================

const styles: Record<string, React.CSSProperties> = {
    input: {
        padding: '4px 8px',
        backgroundColor: '#333',
        border: '1px solid #555',
        borderRadius: '3px',
        color: '#fff',
        fontSize: '12px',
        outline: 'none',
    },
    label: {
        fontSize: '12px',
        color: '#aaa',
        minWidth: '100px',
        flexShrink: 0,
    },
    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
    },
};

// ============================================================================
// Field Editor Components
// ============================================================================

/**
 * InlineNumberInput - number input that defers validation to blur
 * Used in array items where we need local state per-input
 */
const InlineNumberInput: React.FC<{
    value: number | undefined;
    onChange: (value: number | undefined) => void;
    step?: number;
    min?: number;
    max?: number;
    optional?: boolean;
    style?: React.CSSProperties;
}> = ({ value, onChange, step = 0.1, min, max, optional, style }) => {
    const [localValue, setLocalValue] = useState<string>(value?.toString() ?? '');

    useEffect(() => {
        setLocalValue(value?.toString() ?? '');
    }, [value]);

    return (
        <input
            type="number"
            value={localValue}
            onChange={(e) => {
                const text = e.target.value;
                setLocalValue(text);
                if (text !== '' && text !== '-') {
                    const num = parseFloat(text);
                    if (!isNaN(num)) {
                        onChange(num);
                    }
                }
            }}
            onBlur={() => {
                if (localValue === '' || localValue === '-') {
                    if (optional) {
                        onChange(undefined);
                    } else {
                        onChange(0);
                        setLocalValue('0');
                    }
                }
            }}
            step={step}
            min={min}
            max={max}
            style={style}
        />
    );
};

interface FieldProps<T> {
    label: string;
    value: T;
    onChange: (value: T) => void;
    description?: string;
}

const StringField: React.FC<FieldProps<string> & { format?: 'multiline' }> = ({ label, value, onChange, description, format }) => {
    // Handle object values by converting to JSON string for display/edit
    const displayValue = typeof value === 'object' && value !== null
        ? JSON.stringify(value)
        : (value || '');

    const handleChange = (text: string) => {
        // Try to parse as JSON if it looks like an object/array
        if (text.startsWith('{') || text.startsWith('[')) {
            try {
                onChange(JSON.parse(text) as string);
                return;
            } catch {
                // If invalid JSON, pass as string
            }
        }
        onChange(text);
    };

    if (format === 'multiline') {
        return (
            <div style={{ ...styles.fieldRow, alignItems: 'flex-start' }} title={description}>
                <span style={{ ...styles.label, paddingTop: '4px' }}>{label}</span>
                <textarea
                    value={displayValue}
                    onChange={(e) => handleChange(e.target.value)}
                    style={{ ...styles.input, flex: 1, minHeight: '60px', resize: 'vertical', fontFamily: 'inherit' }}
                />
            </div>
        );
    }

    return (
        <div style={styles.fieldRow} title={description}>
            <span style={styles.label}>{label}</span>
            <input
                type="text"
                value={displayValue}
                onChange={(e) => handleChange(e.target.value)}
                style={{ ...styles.input, flex: 1 }}
            />
        </div>
    );
};

const NumberField: React.FC<FieldProps<number | undefined> & { step?: number; min?: number; max?: number; optional?: boolean }> = ({ label, value, onChange, step = 0.1, min, max, description, optional }) => {
    const [localValue, setLocalValue] = useState<string>(value?.toString() ?? '');

    // Sync local value when prop changes (e.g., from undo/redo or external update)
    useEffect(() => {
        setLocalValue(value?.toString() ?? '');
    }, [value]);

    return (
        <div style={styles.fieldRow} title={description}>
            <span style={styles.label}>{label}</span>
            <input
                type="number"
                value={localValue}
                onChange={(e) => {
                    const text = e.target.value;
                    setLocalValue(text);
                    // Only commit valid numbers while typing
                    if (text !== '' && text !== '-') {
                        const num = parseFloat(text);
                        if (!isNaN(num)) {
                            onChange(num);
                        }
                    }
                }}
                onBlur={() => {
                    // On blur, finalize the value
                    if (localValue === '' || localValue === '-') {
                        if (optional) {
                            onChange(undefined);
                        } else {
                            onChange(0);
                            setLocalValue('0');
                        }
                    }
                }}
                step={step}
                min={min}
                max={max}
                style={{ ...styles.input, width: '80px' }}
            />
        </div>
    );
};

const BooleanField: React.FC<FieldProps<boolean>> = ({ label, value, onChange, description }) => (
    <div style={styles.fieldRow} title={description}>
        <span style={styles.label}>{label}</span>
        <input
            type="checkbox"
            checked={value ?? false}
            onChange={(e) => onChange(e.target.checked)}
            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
        />
    </div>
);

const ColorField: React.FC<FieldProps<string>> = ({ label, value, onChange, description }) => {
    // Extract hex color (handle rgba or hex with alpha)
    const hexValue = value?.startsWith('#') ? value.substring(0, 7) : '#ffffff';

    return (
        <div style={styles.fieldRow} title={description}>
            <span style={styles.label}>{label}</span>
            <input
                type="color"
                value={hexValue}
                onChange={(e) => onChange(e.target.value)}
                style={{ width: '40px', height: '24px', padding: 0, border: 'none', cursor: 'pointer' }}
            />
            <input
                type="text"
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                style={{ ...styles.input, width: '100px' }}
                placeholder="#RRGGBB"
            />
        </div>
    );
};

/**
 * A two-number tuple. Unlike Vec3/Vec4 this is not a spatial vector — the
 * fields using it are pairs like a `[min, max]` range — so the inputs are
 * labelled by role and carry no axis colouring.
 */
const Vec2Field: React.FC<FieldProps<[number, number] | undefined> & { optional?: boolean }> = ({ label, value, onChange, description, optional }) => {
    const vec = value ?? [0, 0];
    const labels = ['min', 'max'];

    const handleChange = (index: 0 | 1, val: number) => {
        const updated: [number, number] = [vec[0], vec[1]];
        updated[index] = val;
        onChange(updated);
    };

    return (
        <div style={styles.fieldRow} title={description}>
            <span style={styles.label}>{label}</span>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                {([0, 1] as const).map(i => (
                    <React.Fragment key={i}>
                        <span style={{ color: '#aaa', fontSize: '11px' }}>{labels[i]}</span>
                        <input
                            type="number"
                            value={vec[i]}
                            onChange={(e) => handleChange(i, parseFloat(e.target.value) || 0)}
                            step={0.1}
                            style={{ ...styles.input, width: '62px' }}
                        />
                    </React.Fragment>
                ))}
                {optional && value !== undefined && (
                    <button
                        type="button"
                        onClick={() => onChange(undefined)}
                        title="Clear"
                        style={{ ...styles.input, width: '22px', cursor: 'pointer', padding: 0 }}
                    >x</button>
                )}
            </div>
        </div>
    );
};

const Vec3Field: React.FC<FieldProps<Vec3>> = ({ label, value, onChange, description }) => {
    const vec: Vec3 = value || [0, 0, 0];

    const handleChange = (index: 0 | 1 | 2, val: number) => {
        const updated: Vec3 = [...vec];
        updated[index] = val;
        onChange(updated);
    };

    return (
        <div style={styles.fieldRow} title={description}>
            <span style={styles.label}>{label}</span>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <span style={{ color: '#f88', fontSize: '11px' }}>X</span>
                <input
                    type="number"
                    value={vec[0].toFixed(2)}
                    onChange={(e) => handleChange(0, parseFloat(e.target.value) || 0)}
                    step={0.1}
                    style={{ ...styles.input, width: '62px' }}
                />
                <span style={{ color: '#8f8', fontSize: '11px' }}>Y</span>
                <input
                    type="number"
                    value={vec[1].toFixed(2)}
                    onChange={(e) => handleChange(1, parseFloat(e.target.value) || 0)}
                    step={0.1}
                    style={{ ...styles.input, width: '62px' }}
                />
                <span style={{ color: '#88f', fontSize: '11px' }}>Z</span>
                <input
                    type="number"
                    value={vec[2].toFixed(2)}
                    onChange={(e) => handleChange(2, parseFloat(e.target.value) || 0)}
                    step={0.1}
                    style={{ ...styles.input, width: '62px' }}
                />
            </div>
        </div>
    );
};

const Vec4Field: React.FC<FieldProps<Vec4>> = ({ label, value, onChange, description }) => {
    const vec: Vec4 = value || [0, 0, 0, 1];
    const labels = ['X', 'Y', 'Z', 'W'];
    const colors = ['#f88', '#8f8', '#88f', '#fa8'];

    const handleChange = (index: 0 | 1 | 2 | 3, val: number) => {
        const updated: Vec4 = [...vec];
        updated[index] = val;
        onChange(updated);
    };

    return (
        <div style={styles.fieldRow} title={description}>
            <span style={styles.label}>{label}</span>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                {([0, 1, 2, 3] as const).map(i => (
                    <React.Fragment key={i}>
                        <span style={{ color: colors[i], fontSize: '11px' }}>{labels[i]}</span>
                        <input
                            type="number"
                            value={vec[i].toFixed(3)}
                            onChange={(e) => handleChange(i, parseFloat(e.target.value) || 0)}
                            step={0.01}
                            style={{ ...styles.input, width: '56px' }}
                        />
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

const EnumField: React.FC<FieldProps<string> & { options: string[] }> = ({ label, value, onChange, options, description }) => (
    <div style={styles.fieldRow} title={description}>
        <span style={styles.label}>{label}</span>
        <select
            value={value || options[0]}
            onChange={(e) => onChange(e.target.value)}
            style={{ ...styles.input, flex: 1, cursor: 'pointer' }}
        >
            {options.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
            ))}
        </select>
    </div>
);

/**
 * MultiSelectField - toggleable buttons for selecting multiple options from a set
 * Used for fields like axes: ("x" | "y" | "z")[]
 */
const MultiSelectField: React.FC<FieldProps<string[]> & { options: string[] }> = ({ label, value, onChange, options, description }) => {
    const selected = value || [];
    const toggle = (opt: string) => {
        if (selected.includes(opt)) {
            onChange(selected.filter(v => v !== opt));
        } else {
            onChange([...selected, opt]);
        }
    };

    return (
        <div style={styles.fieldRow} title={description}>
            <span style={styles.label}>{label}</span>
            <div style={{ display: 'flex', gap: '4px' }}>
                {options.map((opt) => (
                    <button
                        key={opt}
                        onClick={() => toggle(opt)}
                        style={{
                            padding: '2px 10px',
                            fontSize: '12px',
                            border: '1px solid #555',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            backgroundColor: selected.includes(opt) ? '#4a6fa5' : '#333',
                            color: selected.includes(opt) ? '#fff' : '#888',
                        }}
                    >
                        {opt.toUpperCase()}
                    </button>
                ))}
            </div>
        </div>
    );
};

/**
 * ObjectField - renders a collapsible section with nested fields
 */
interface ObjectFieldProps {
    label: string;
    value: Record<string, unknown>;
    onChange: (value: Record<string, unknown>) => void;
    fields: FieldSchema[];
    description?: string;
}

const ObjectField: React.FC<ObjectFieldProps> = ({ label, value, onChange, fields, description }) => {
    const [expanded, setExpanded] = useState(true);

    const handleNestedChange = (key: string, newValue: unknown) => {
        onChange({ ...value, [key]: newValue });
    };

    const renderNestedField = (field: FieldSchema) => {
        const data = value ?? {};
        const onFieldChange = (v: unknown) => handleNestedChange(field.key, v);

        // `vec2` postdates our trace-viewer pin's FieldSchema union, so it cannot
        // be a `case` yet — and this switch's `default: return null` would render
        // the field as NOTHING rather than fall back to a text box. Checked here
        // so a 2-tuple stays authorable both before and after the pin catches up.
        if ((field.type as string) === 'vec2') {
            return <Vec2Field key={field.key} label={field.label} value={fieldValue(data, field) as unknown as [number, number] | undefined} onChange={onFieldChange} description={field.description} optional={field.optional} />;
        }
        switch (field.type) {
            case 'string':
                return <StringField key={field.key} label={field.label} value={fieldValue(data, field)} onChange={onFieldChange} description={field.description} format={field.format} />;
            case 'number':
                return <NumberField key={field.key} label={field.label} value={fieldValue(data, field)} onChange={onFieldChange} step={field.step} description={field.description} optional={field.optional} />;
            case 'boolean':
                return <BooleanField key={field.key} label={field.label} value={fieldValue(data, field)} onChange={onFieldChange} description={field.description} />;
            case 'color':
                return <ColorField key={field.key} label={field.label} value={fieldValue(data, field)} onChange={onFieldChange} description={field.description} />;
            case 'vec3':
                return <Vec3Field key={field.key} label={field.label} value={fieldValue(data, field)} onChange={onFieldChange} description={field.description} />;
            case 'vec4':
                return <Vec4Field key={field.key} label={field.label} value={fieldValue(data, field)} onChange={onFieldChange} description={field.description} />;
            case 'enum':
                return <EnumField key={field.key} label={field.label} value={fieldValue(data, field)} onChange={onFieldChange} options={field.options} description={field.description} />;
            case 'multiSelect':
                return <MultiSelectField key={field.key} label={field.label} value={fieldValue(data, field)} onChange={onFieldChange} options={field.options} description={field.description} />;
            case 'object':
                return (
                    <ObjectField
                        key={field.key}
                        label={field.label}
                        value={fieldValue(data, field) ?? {}}
                        onChange={onFieldChange}
                        fields={field.fields}
                        description={field.description}
                    />
                );
            case 'array':
                if (field.itemFields) {
                    return (
                        <ArrayField
                            key={field.key}
                            label={field.label}
                            value={fieldValue(data, field) ?? []}
                            onChange={onFieldChange}
                            itemFields={field.itemFields}
                            itemLabel={field.itemLabel}
                            description={field.description}
                        />
                    );
                }
                return null;
            default:
                return null;
        }
    };

    return (
        <div style={{
            marginBottom: '8px',
            backgroundColor: '#363636',
            borderRadius: '4px',
            overflow: 'hidden',
        }}>
            <div
                onClick={() => setExpanded(!expanded)}
                title={description}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 10px',
                    backgroundColor: '#404040',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                <span style={{
                    marginRight: '6px',
                    fontSize: '9px',
                    color: '#888',
                    transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s',
                }}>
                    ▶
                </span>
                <span style={{ fontSize: '12px', color: '#ccc' }}>{label}</span>
            </div>
            {expanded && (
                <div style={{ padding: '8px 10px' }}>
                    {fields.map(renderNestedField)}
                </div>
            )}
        </div>
    );
};

/**
 * ArrayField - renders a reorderable list of items with nested fields
 */
interface ArrayFieldProps {
    label: string;
    value: Record<string, unknown>[];
    onChange: (value: Record<string, unknown>[]) => void;
    itemFields: FieldSchema[];
    itemLabel?: string;
    description?: string;
}

const ArrayField: React.FC<ArrayFieldProps> = ({ label, value, onChange, itemFields, itemLabel = 'Item', description }) => {
    const [expanded, setExpanded] = useState(true);
    const items = value || [];

    const handleItemChange = (index: number, key: string, newValue: unknown) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], [key]: newValue };
        onChange(newItems);
    };

    const handleRemoveItem = (index: number) => {
        const newItems = items.filter((_, i) => i !== index);
        onChange(newItems);
    };

    const handleAddItem = () => {
        const newItem: Record<string, unknown> = {};
        itemFields.forEach(field => {
            if (field.default !== undefined) {
                newItem[field.key] = field.default;
            } else if (field.type === 'number') {
                newItem[field.key] = 0;
            } else if (field.type === 'string') {
                newItem[field.key] = '';
            } else if (field.type === 'color') {
                newItem[field.key] = '#FFFFFFFF';
            } else if (field.type === 'boolean') {
                newItem[field.key] = false;
            }
        });
        onChange([...items, newItem]);
    };

    const handleMoveItem = (index: number, direction: 'up' | 'down') => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= items.length) return;
        const newItems = [...items];
        [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
        onChange(newItems);
    };

    const renderItemField = (item: Record<string, unknown>, itemIndex: number, field: FieldSchema) => {
        const onFieldChange = (val: unknown) => handleItemChange(itemIndex, field.key, val);

        // Render inline fields for array items (more compact)
        switch (field.type) {
            case 'number':
                return (
                    <div key={field.key} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ fontSize: '11px', color: '#888', minWidth: '32px' }}>{field.label}</span>
                        <InlineNumberInput
                            value={fieldValue(item, field)}
                            onChange={onFieldChange}
                            step={field.step ?? 0.01}
                            min={field.min}
                            max={field.max}
                            optional={field.optional}
                            style={{ ...styles.input, width: '60px' }}
                        />
                    </div>
                );
            case 'color': {
                const colorStr = fieldValue(item, field);
                const hexValue = colorStr?.startsWith('#') ? colorStr.substring(0, 7) : '#ffffff';
                return (
                    <div key={field.key} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ fontSize: '11px', color: '#888', minWidth: '32px' }}>{field.label}</span>
                        <input
                            type="color"
                            value={hexValue}
                            onChange={(e) => onFieldChange(e.target.value + 'FF')}
                            style={{ width: '28px', height: '20px', padding: 0, border: '1px solid #555', borderRadius: '2px', cursor: 'pointer' }}
                        />
                    </div>
                );
            }
            case 'string':
                return (
                    <div key={field.key} style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: 1 }}>
                        <span style={{ fontSize: '11px', color: '#888', minWidth: '32px' }}>{field.label}</span>
                        <input
                            type="text"
                            value={fieldValue(item, field) || ''}
                            onChange={(e) => onFieldChange(e.target.value)}
                            style={{ ...styles.input, flex: 1, minWidth: '60px' }}
                        />
                    </div>
                );
            case 'boolean':
                // A boolean in an array item rendered as NOTHING — the field
                // fell to `default` (#243). Same inline row as the others.
                return (
                    <div key={field.key} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ fontSize: '11px', color: '#888', minWidth: '32px' }}>{field.label}</span>
                        <input
                            type="checkbox"
                            checked={Boolean(fieldValue(item, field))}
                            onChange={(e) => onFieldChange(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div style={{
            marginBottom: '8px',
            backgroundColor: '#363636',
            borderRadius: '4px',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <div
                onClick={() => setExpanded(!expanded)}
                title={description}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 10px',
                    backgroundColor: '#404040',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                <span style={{
                    marginRight: '6px',
                    fontSize: '9px',
                    color: '#888',
                    transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s',
                }}>
                    ▶
                </span>
                <span style={{ fontSize: '12px', color: '#ccc', flex: 1 }}>{label}</span>
                <span style={{ fontSize: '11px', color: '#666' }}>{items.length}</span>
            </div>

            {expanded && (
                <div style={{ padding: '8px' }}>
                    {/* Items */}
                    {items.map((item, index) => (
                        <div
                            key={index}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 8px',
                                backgroundColor: '#2a2a2a',
                                borderRadius: '3px',
                                marginBottom: '4px',
                            }}
                        >
                            {/* Reorder buttons */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                <button
                                    onClick={() => handleMoveItem(index, 'up')}
                                    disabled={index === 0}
                                    style={{
                                        padding: '0',
                                        width: '14px',
                                        height: '10px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        color: index === 0 ? '#444' : '#888',
                                        cursor: index === 0 ? 'default' : 'pointer',
                                        fontSize: '8px',
                                        lineHeight: 1,
                                    }}
                                    title="Move up"
                                >
                                    ▲
                                </button>
                                <button
                                    onClick={() => handleMoveItem(index, 'down')}
                                    disabled={index === items.length - 1}
                                    style={{
                                        padding: '0',
                                        width: '14px',
                                        height: '10px',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        color: index === items.length - 1 ? '#444' : '#888',
                                        cursor: index === items.length - 1 ? 'default' : 'pointer',
                                        fontSize: '8px',
                                        lineHeight: 1,
                                    }}
                                    title="Move down"
                                >
                                    ▼
                                </button>
                            </div>

                            {/* Item fields */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, flexWrap: 'wrap' }}>
                                {itemFields.map(field => renderItemField(item, index, field))}
                            </div>

                            {/* Remove button */}
                            <button
                                onClick={() => handleRemoveItem(index)}
                                style={{
                                    padding: '2px 6px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    color: '#666',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                }}
                                title={`Remove ${itemLabel.toLowerCase()}`}
                            >
                                ×
                            </button>
                        </div>
                    ))}

                    {/* Add button */}
                    <button
                        onClick={handleAddItem}
                        style={{
                            width: '100%',
                            padding: '6px',
                            backgroundColor: '#3a3a3a',
                            border: '1px dashed #555',
                            borderRadius: '3px',
                            color: '#888',
                            cursor: 'pointer',
                            fontSize: '11px',
                            marginTop: items.length > 0 ? '4px' : 0,
                        }}
                    >
                        + Add {itemLabel}
                    </button>
                </div>
            )}
        </div>
    );
};


// ============================================================================
// Field Grouping
// ============================================================================

/**
 * Renders fields organized by @group tags. Fields without a group render first
 * (ungrouped), then each named group renders as a collapsible section.
 */
function renderFieldsGrouped(
    fields: FieldSchema[],
    renderField: (field: FieldSchema) => React.ReactNode,
): React.ReactNode {
    // Check if any field has a group
    const hasGroups = fields.some(f => f.group);
    if (!hasGroups) return fields.map(renderField);

    // Partition into ordered groups preserving field order.
    // Note: fields with the same @group that are non-contiguous will render as
    // separate collapsible sections. This is fine since schemas define field order.
    const groups: { name: string | null; fields: FieldSchema[] }[] = [];
    let currentGroup: { name: string | null; fields: FieldSchema[] } | null = null;

    for (const field of fields) {
        const groupName = field.group ?? null;
        if (!currentGroup || currentGroup.name !== groupName) {
            currentGroup = { name: groupName, fields: [] };
            groups.push(currentGroup);
        }
        currentGroup.fields.push(field);
    }

    return groups.map((group, i) => {
        if (group.name === null) {
            return <React.Fragment key={`ungrouped-${i}`}>{group.fields.map(renderField)}</React.Fragment>;
        }
        return <FieldGroup key={group.name} label={group.name}>{group.fields.map(renderField)}</FieldGroup>;
    });
}

const FieldGroup: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
    const [expanded, setExpanded] = useState(true);
    return (
        <div style={{ marginBottom: '4px' }}>
            <div
                onClick={() => setExpanded(!expanded)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 0',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#888',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    userSelect: 'none',
                }}
            >
                <span style={{
                    transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s',
                    fontSize: '8px',
                }}>
                    ▶
                </span>
                {label}
            </div>
            {expanded && (
                <div style={{ paddingLeft: '4px' }}>
                    {children}
                </div>
            )}
        </div>
    );
};

// ============================================================================
// BehaviorSection Component
// ============================================================================

interface BehaviorSectionProps {
    behavior: TraceBehavior;
    onRemove: () => void;
    onSaved: (behaviorType: string) => void;
    onEditScript?: (behavior: ScriptBehavior) => void;
}

export const BehaviorSection: React.FC<BehaviorSectionProps> = ({ behavior, onRemove, onSaved, onEditScript }) => {
    const [expanded, setExpanded] = useState(true);
    // The data the editor shows/edits. For Properties under a selected keyframe,
    // the declaration values are overlaid with that keyframe's stored values, so
    // the value fields read and write a SINGLE synchronous source (typing updates
    // it immediately; a keyframe switch reseeds it). At Base the overlay is a
    // no-op and this is just behavior.getData().
    const getDisplayData = useCallback(
        // Overlay keyframe AND active-state values onto the declarations so the
        // panel's fields show the active pose's value (not Base) while a keyframe
        // or group state is active. (Keyframe first; if no keyframe, state.)
        (): BehaviorData => overlayStateValues(
            behavior.sceneObject,
            overlayKeyframeValues(behavior.sceneObject, behavior.getData()),
        ),
        [behavior],
    );
    const [localData, setLocalData] = useState<BehaviorData>(getDisplayData);
    const [error, setError] = useState<string | null>(null);
    // JSON editor needs explicit save since typing is continuous
    const [jsonDirty, setJsonDirty] = useState(false);
    const [jsonSaving, setJsonSaving] = useState(false);
    // Track raw JSON text separately to allow invalid intermediate states while typing
    const [jsonText, setJsonText] = useState(() => JSON.stringify(behavior.getData(), null, 2));
    const [jsonParseError, setJsonParseError] = useState<string | null>(null);

    const behaviorType = localData['@type'];
    const builtInSchema = BEHAVIOR_SCHEMAS[behaviorType];
    // For script behaviors, try the inferred schema from ScriptRegistry
    const scriptSchema = behavior instanceof ScriptBehavior
        ? behavior.sceneObject.traceScene.scriptRegistry.getSchema(behaviorType)
        : undefined;
    const schema = builtInSchema ?? (scriptSchema && scriptSchema.length > 0 ? scriptSchema : undefined);
    const hasSchema = schema !== undefined;

    // Reset local data when behavior changes
    useEffect(() => {
        const data = getDisplayData();
        setLocalData(data);
        setJsonText(JSON.stringify(data, null, 2));
        setError(null);
        setJsonDirty(false);
        setJsonParseError(null);
    }, [behavior, getDisplayData]);

    // Reseed local data when the keyframe overlay changes the selected keyframe,
    // so the value fields snap to the newly-selected keyframe's stored values.
    // (The behavior instance doesn't change on a keyframe switch, so the reset
    // effect above doesn't re-fire — this is what carries the switch through.)
    useEffect(() => {
        const handleSelectionChanged = () => {
            const data = getDisplayData();
            setLocalData(data);
            setJsonText(JSON.stringify(data, null, 2));
        };
        window.addEventListener(EDITOR_EVENTS.KEYFRAME_SELECTION_CHANGED, handleSelectionChanged);
        return () => window.removeEventListener(EDITOR_EVENTS.KEYFRAME_SELECTION_CHANGED, handleSelectionChanged);
    }, [getDisplayData]);

    // Refresh local data when this behavior is modified externally (e.g. from popup editor)
    const selfCommittingRef = useRef(false);
    useEffect(() => {
        const handleExternalChange = (event: Event) => {
            if (selfCommittingRef.current) return;
            const detail = (event as CustomEvent<BehaviorChangedDetail>).detail;
            if (detail.behaviorType === behaviorType) {
                const data = getDisplayData();
                setLocalData(data);
                setJsonText(JSON.stringify(data, null, 2));
            }
        };
        window.addEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, handleExternalChange);
        return () => window.removeEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, handleExternalChange);
    }, [behavior, behaviorType, getDisplayData]);

    // Refresh local data when Properties values change via external commit (e.g. AutoUI).
    // Uses the existing onValueChanged callback on Properties. Only refreshes React
    // state — does NOT re-commit.
    useEffect(() => {
        if (!(behavior instanceof Properties)) return;
        const props = behavior;
        props.onValueChanged = () => {
            if (selfCommittingRef.current) return;
            const data = getDisplayData();
            setLocalData(data);
            setJsonText(JSON.stringify(data, null, 2));
        };
        return () => { props.onValueChanged = null; };
    }, [behavior, getDisplayData]);

    // Show collider debug visualization when the section is expanded.
    // Skip in XR — collider wireframes don't render correctly in stereo.
    const isCollider = behavior instanceof BoxCollider || behavior instanceof SphereCollider;
    useEffect(() => {
        if (!isCollider) return;
        const collider = behavior;
        const eventSystem = behavior.sceneObject?.traceScene?.getEventSystem?.();
        const isXR = (eventSystem as unknown as { xrState?: string })?.xrState === 'active';
        if (expanded && !isXR) {
            collider.setDebugVisible(true);
        }
        return () => { collider.setDebugVisible(false); };
    }, [behavior, expanded, isCollider]);

    // Live commit - commits data immediately.
    //
    // `selfCommit` runs a step with `selfCommittingRef` set and clears it in a
    // finally, even if `behavior.commit()` or `onSaved()` throws. A flag left
    // set would silently suppress both the external BEHAVIOR_CHANGED listener
    // and the Properties onValueChanged callback for this section's lifetime.
    //
    // TODO: the whole ref-flag mechanism exists because
    // `dispatchBehaviorChanged` fires synchronously mid-commit. Tracked as
    // follow-up #6 in EDITOR_CORE_SHARING_PLAN.md — switching dispatch to
    // `queueMicrotask` would let these listeners run after the commit is
    // complete and the flag could be removed entirely.
    const selfCommit = async <T,>(fn: () => T | Promise<T>): Promise<T> => {
        selfCommittingRef.current = true;
        try {
            return await fn();
        } finally {
            selfCommittingRef.current = false;
        }
    };

    const liveCommit = async (newData: BehaviorData, fieldKey?: string) => {
        try {
            // A scene never edits the inside of a reference (SCENE_ROOT_SPEC
            // §2.3 "Editing"): a behavior changed on a descendant of a
            // placement copies the asset into this scene first, and the commit
            // below then lands on a node this scene owns. The object keeps its
            // identity across the copy, so `behavior` is still the right one.
            if (!await copyForInsideEdit(behavior.sceneObject)) return;

            const beforeData = behavior.getData();
            // A Properties value edit while a state is showing goes into that
            // state and never through Base, unless a keyframe is selected
            // (the keyframe capture below takes it); anything else is committed below,
            // and the fields then show the showing state's (`stateTarget`) values.
            let stateTarget: string | undefined;
            if (behavior instanceof Properties) {
                const route = await selfCommit(() => routePropertyEditIntoState(
                    behavior.sceneObject, localData as PropertiesData, newData as PropertiesData));
                if ('captured' in route) {
                    // The captured edit over the live states map (a later states
                    // edit must not drop it), or what is showing when refused.
                    setLocalData(route.show);
                    setError(null);
                    await selfCommit(() => onSaved(behaviorType));
                    return;
                }
                newData = route.base;
                stateTarget = route.target;
            }

            // Don't let a PROPERTY edit clobber state overrides written externally
            // (e.g. a transform the gizmo just wrote into the active state). The
            // panel's `localData.states` can be stale relative to the live behavior;
            // committing `{ ...localData, states }` would shallow-merge that stale
            // map and drop the external override → the object jumps to base.
            if (behavior instanceof Properties) {
                newData = reconcilePropertyEditStates(
                    newData as PropertiesData,
                    localData as PropertiesData,
                    beforeData as PropertiesData,
                );
            }
            const errors = await selfCommit(() => behavior.commit(newData));

            if (errors.length > 0) {
                setError(errors.map(e => e.message).join(', '));
                return;
            }

            // Refresh from behavior to get normalized data
            const afterData = behavior.getData();
            setError(null);

            // A Properties edit made while a keyframe is selected must be routed
            // into that keyframe, not left on Base. Do this BEFORE onSaved (which
            // dispatches BEHAVIOR_CHANGED → the always-on capture listener):
            // capturePropertyEditIntoKeyframe reverts the Base write and returns
            // the keyframe undo command. By the time the listener fires, the
            // declaration matches its reverted baseline so the listener's diff is
            // empty and it no-ops — no double capture. When it returns a command
            // we push ONLY that (one atomic undo entry); the Base write was
            // reverted to a no-op, so recording the Properties command too would
            // let redo re-leak the edit into Base.
            //
            // Guard with selfCommittingRef: the capture's internal revert commit
            // (props.commit of the reverted-to-baseline declarations) fires
            // Properties.onValueChanged, whose handler would otherwise overwrite
            // localData with the reverted baseline — making the input field snap
            // back to the OLD value while the viewport (driven by the keyframe
            // contribution) shows the NEW one. The user's typed value lives in
            // localData already; we keep it.
            let keyframeCommand: BehaviorCommitCommand | null = null;
            if (behaviorType === 'Properties' && !getUndoManager().isUndoRedoing) {
                keyframeCommand = await selfCommit(() => capturePropertyEditIntoKeyframe(behavior.sceneObject));
                // When the edit was captured into a keyframe, the Base
                // declarations are now reverted to baseline. Keep localData as the
                // user's input (set by the field's onChange before liveCommit ran)
                // so the input fields keep showing the edited value. While a
                // state shows (the commit keeps it), the fields show its values.
                if (stateTarget !== undefined && behavior instanceof Properties) {
                    if (!keyframeCommand) setLocalData(getDisplayData());
                } else if (!keyframeCommand) {
                    setLocalData(afterData);
                }
            } else {
                setLocalData(afterData);
            }

            await selfCommit(() => onSaved(behaviorType));

            // Re-enable collider debug viz after commit (commit recreates the picker mesh)
            if (isCollider && expanded) {
                (behavior).setDebugVisible(true);
            }

            // Record undo command
            if (!getUndoManager().isUndoRedoing) {
                if (keyframeCommand) {
                    getUndoManager().push(keyframeCommand);
                } else {
                    getUndoManager().push(new BehaviorCommitCommand(
                        behavior.sceneObject,
                        behaviorType,
                        beforeData,
                        afterData,
                        fieldKey ?? null,
                    ));
                }
            }
        } catch (err) {
            setError((err as Error).message);
        }
    };

    // Field change handler - updates local state and commits immediately
    const handleFieldChange = (key: string, value: unknown) => {
        const newData = { ...localData, [key]: value };
        setLocalData(newData);
        setError(null);
        void liveCommit(newData, key);
    };

    // JSON editor change handler - updates raw text and tries to parse
    const handleJsonTextChange = (text: string) => {
        setJsonText(text);
        setJsonDirty(true);
        setError(null);

        // Try to parse - if valid, update localData; if not, just track the parse error
        try {
            const parsed = JSON.parse(text) as BehaviorData;
            setLocalData(parsed);
            setJsonParseError(null);
        } catch (e) {
            setJsonParseError((e as SyntaxError).message);
        }
    };

    // JSON editor save
    const handleJsonSave = async () => {
        setJsonSaving(true);
        await liveCommit(localData);
        setJsonText(JSON.stringify(behavior.getData(), null, 2));
        setJsonDirty(false);
        setJsonSaving(false);
    };

    // JSON editor revert
    const handleJsonRevert = () => {
        const data = behavior.getData();
        setLocalData(data);
        setJsonText(JSON.stringify(data, null, 2));
        setJsonDirty(false);
        setJsonParseError(null);
        setError(null);
    };

    const renderField = (field: FieldSchema) => {
        const data = localData as BehaviorData & Record<string, unknown>;
        const onChange = (val: unknown) => handleFieldChange(field.key, val);

        // `vec2` postdates our trace-viewer pin's FieldSchema union, so it cannot
        // be a `case` yet — and this switch's `default: return null` would render
        // the field as NOTHING rather than fall back to a text box. Checked here
        // so a 2-tuple stays authorable both before and after the pin catches up.
        if ((field.type as string) === 'vec2') {
            return <Vec2Field key={field.key} label={field.label} value={fieldValue(data, field) as unknown as [number, number] | undefined} onChange={onChange} description={field.description} optional={field.optional} />;
        }
        switch (field.type) {
            case 'string':
                return <StringField key={field.key} label={field.label} value={fieldValue(data, field)} onChange={onChange} description={field.description} format={field.format} />;
            case 'number':
                return <NumberField key={field.key} label={field.label} value={fieldValue(data, field)} onChange={onChange} step={field.step} min={field.min} max={field.max} description={field.description} optional={field.optional} />;
            case 'boolean':
                return <BooleanField key={field.key} label={field.label} value={fieldValue(data, field)} onChange={onChange} description={field.description} />;
            case 'color':
                return <ColorField key={field.key} label={field.label} value={fieldValue(data, field)} onChange={onChange} description={field.description} />;
            case 'vec3':
                return <Vec3Field key={field.key} label={field.label} value={fieldValue(data, field)} onChange={onChange} description={field.description} />;
            case 'enum':
                return <EnumField key={field.key} label={field.label} value={fieldValue(data, field)} onChange={onChange} options={field.options} description={field.description} />;
            case 'multiSelect':
                return <MultiSelectField key={field.key} label={field.label} value={fieldValue(data, field)} onChange={onChange} options={field.options} description={field.description} />;
            case 'object':
                return (
                    <ObjectField
                        key={field.key}
                        label={field.label}
                        value={fieldValue(data, field) ?? {}}
                        onChange={onChange}
                        fields={field.fields}
                        description={field.description}
                    />
                );
            case 'array':
                if (field.itemFields) {
                    return (
                        <ArrayField
                            key={field.key}
                            label={field.label}
                            value={fieldValue(data, field) ?? []}
                            onChange={onChange}
                            itemFields={field.itemFields}
                            itemLabel={field.itemLabel}
                            description={field.description}
                        />
                    );
                }
                return null;
            case 'asset':
                return (
                    <AssetField
                        key={field.key}
                        label={field.label}
                        value={fieldValue(data, field) || ''}
                        onChange={onChange}
                        assetType={field.assetType}
                        resources={behavior.sceneObject.resources}
                        description={field.description}
                    />
                );
            default:
                return null;
        }
    };

    const behaviorDescription = getBehaviorDescription(behaviorType);

    return (
        <div style={{
            marginBottom: '8px',
            backgroundColor: '#2d2d2d',
            borderRadius: '4px',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 12px',
                    backgroundColor: '#383838',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
                onClick={() => setExpanded(!expanded)}
                title={behaviorDescription}
            >
                <span style={{
                    marginRight: '8px',
                    fontSize: '10px',
                    color: '#888',
                    transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s',
                }}>
                    ▶
                </span>
                <span style={{ flex: 1, fontSize: '13px', fontWeight: 500 }}>
                    {behaviorType}
                </span>
                <button
                    onClick={(e) => { e.stopPropagation(); onRemove(); }}
                    style={{
                        padding: '2px 6px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#888',
                        cursor: 'pointer',
                        fontSize: '14px',
                        lineHeight: 1,
                    }}
                    title="Remove behavior"
                >
                    ×
                </button>
            </div>

            {/* Body */}
            {expanded && (
                <div style={{ padding: '12px' }}>
                    {behavior instanceof Properties ? (
                        <PropertiesEditor
                            behavior={behavior}
                            declarations={(localData as PropertiesData).properties}
                            sheets={(localData as PropertiesData).sheets}
                            states={(localData as PropertiesData).states ?? {}}
                            onChange={(properties, states) => {
                                const newData = { ...localData, properties, states };
                                setLocalData(newData);
                                setError(null);
                                void liveCommit(newData);
                            }}
                            onSheetsChange={(sheets) => {
                                const newData = { ...localData, sheets };
                                setLocalData(newData);
                                setError(null);
                                void liveCommit(newData);
                            }}
                            onStatesChange={(states) => {
                                const newData = { ...localData, states };
                                setLocalData(newData);
                                setError(null);
                                void liveCommit(newData);
                            }}
                            initialState={(localData as PropertiesData).initialState}
                            onInitialStateChange={(initialState) => {
                                const newData = { ...localData, initialState };
                                setLocalData(newData);
                                setError(null);
                                void liveCommit(newData);
                            }}
                        />
                    ) : behavior instanceof Renderer ? (
                        <RendererEditor
                            data={localData as RendererData}
                            behavior={behavior}
                            onChange={(data) => {
                                setLocalData(data);
                                // RendererEditor handles its own live commits
                            }}
                        />
                    ) : behavior instanceof Animation ? (
                        <AnimationEditor
                            data={localData as AnimationData}
                            behavior={behavior}
                            onChange={(data) => {
                                setLocalData(data);
                                setError(null);
                                void liveCommit(data);
                            }}
                            onCreateClip={() => {
                                window.dispatchEvent(new CustomEvent(EDITOR_EVENTS.CREATE_CLIP));
                            }}
                            onEditClip={() => {
                                window.dispatchEvent(new CustomEvent(EDITOR_EVENTS.EDIT_CLIP));
                            }}
                            onClipSelected={(clipName: string) => {
                                window.dispatchEvent(new CustomEvent(EDITOR_EVENTS.CLIP_SELECTED, { detail: { clipName } }));
                            }}
                        />
                    ) : behavior instanceof AnimationSequence ? (
                        <AnimationSequenceEditor
                            data={localData as AnimationSequenceData}
                            behavior={behavior}
                            onChange={(data) => {
                                setLocalData(data);
                                setError(null);
                                void liveCommit(data);
                            }}
                        />
                    ) : behavior instanceof Keyframes ? (
                        <KeyframesEditor
                            data={localData as KeyframesData}
                            behavior={behavior}
                            onChange={(data) => {
                                setLocalData(data);
                                setError(null);
                                void liveCommit(data);
                            }}
                        />
                    ) : behavior instanceof Pages ? (
                        <PagesEditor
                            data={localData as PagesData}
                            behavior={behavior}
                            onChange={(data) => {
                                setLocalData(data);
                                setError(null);
                                void liveCommit(data);
                            }}
                        />
                    ) : behavior instanceof BehaviorGraph ? (
                        <BehaviorGraphEditorComponent data={localData as BehaviorGraphData} />
                    ) : behavior instanceof ParticleSystem ? (
                        <ParticleSystemEditor
                            data={localData as ParticleSystemData}
                            behavior={behavior}
                            onChange={(data) => {
                                setLocalData(data);
                                setError(null);
                                void liveCommit(data);
                            }}
                        />
                    ) : behavior instanceof Synth ? (
                        <SynthEditor
                            data={localData as SynthData}
                            behavior={behavior}
                            onChange={(data) => {
                                setLocalData(data);
                                setError(null);
                                void liveCommit(data);
                            }}
                        />
                    ) : behavior instanceof Timeline ? (
                        <TimelineEditor
                            data={localData as TimelineData}
                            behavior={behavior}
                            onChange={(data) => {
                                setLocalData(data);
                                setError(null);
                                void liveCommit(data);
                            }}
                        />
                    ) : behavior instanceof ScriptBehavior ? (
                        /* Script behavior — schema-driven data fields + Edit Script button at bottom */
                        <>
                            {hasSchema && schema.length > 0 ? (
                                renderFieldsGrouped(schema, renderField)
                            ) : hasSchema && schema.length === 0 ? (
                                <div style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                                    No configurable properties
                                </div>
                            ) : (
                                /* Fallback to JSON editor when no schema inferred */
                                <>
                                    <JsonEditor
                                        value={jsonText}
                                        onChange={handleJsonTextChange}
                                        minHeight="100px"
                                    />
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                        <button
                                            onClick={handleJsonSave}
                                            disabled={!jsonDirty || jsonSaving || !!jsonParseError}
                                            style={{
                                                flex: 1,
                                                padding: '6px 12px',
                                                backgroundColor: jsonDirty && !jsonParseError ? '#0078d4' : '#444',
                                                border: 'none',
                                                borderRadius: '3px',
                                                color: '#fff',
                                                cursor: jsonDirty && !jsonParseError ? 'pointer' : 'not-allowed',
                                                fontSize: '12px',
                                                opacity: jsonSaving ? 0.7 : 1,
                                            }}
                                        >
                                            {jsonSaving ? 'Saving...' : 'Save'}
                                        </button>
                                        <button
                                            onClick={handleJsonRevert}
                                            disabled={!jsonDirty}
                                            style={{
                                                padding: '6px 12px',
                                                backgroundColor: jsonDirty ? '#555' : '#444',
                                                border: 'none',
                                                borderRadius: '3px',
                                                color: '#fff',
                                                cursor: jsonDirty ? 'pointer' : 'not-allowed',
                                                fontSize: '12px',
                                            }}
                                        >
                                            Revert
                                        </button>
                                    </div>
                                    {(jsonParseError ?? error) && (
                                        <div style={{
                                            marginTop: '8px',
                                            padding: '6px 10px',
                                            backgroundColor: '#ff5252',
                                            color: '#fff',
                                            borderRadius: '3px',
                                            fontSize: '11px'
                                        }}>
                                            {jsonParseError ?? error}
                                        </div>
                                    )}
                                </>
                            )}
                            <button
                                onClick={() => onEditScript?.(behavior)}
                                style={{
                                    width: '100%',
                                    padding: '6px 12px',
                                    marginTop: '12px',
                                    backgroundColor: 'transparent',
                                    border: '1px solid #4a4a4a',
                                    borderRadius: '3px',
                                    color: '#9cb',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    textAlign: 'center',
                                }}
                            >
                                Edit Script
                            </button>
                        </>
                    ) : hasSchema && schema.length > 0 ? (
                        renderFieldsGrouped(schema, renderField)
                    ) : hasSchema && schema.length === 0 ? (
                        <div style={{ color: '#666', fontSize: '12px', fontStyle: 'italic' }}>
                            No configurable properties
                        </div>
                    ) : (
                        /* JSON editor - needs explicit save due to continuous typing */
                        <>
                            <JsonEditor
                                value={jsonText}
                                onChange={handleJsonTextChange}
                                minHeight="100px"
                            />
                            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                <button
                                    onClick={handleJsonSave}
                                    disabled={!jsonDirty || jsonSaving || !!jsonParseError}
                                    style={{
                                        flex: 1,
                                        padding: '6px 12px',
                                        backgroundColor: jsonDirty && !jsonParseError ? '#0078d4' : '#444',
                                        border: 'none',
                                        borderRadius: '3px',
                                        color: '#fff',
                                        cursor: jsonDirty && !jsonParseError ? 'pointer' : 'not-allowed',
                                        fontSize: '12px',
                                        opacity: jsonSaving ? 0.7 : 1,
                                    }}
                                >
                                    {jsonSaving ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                    onClick={handleJsonRevert}
                                    disabled={!jsonDirty}
                                    style={{
                                        padding: '6px 12px',
                                        backgroundColor: jsonDirty ? '#555' : '#444',
                                        border: 'none',
                                        borderRadius: '3px',
                                        color: '#fff',
                                        cursor: jsonDirty ? 'pointer' : 'not-allowed',
                                        fontSize: '12px',
                                    }}
                                >
                                    Revert
                                </button>
                            </div>
                            {(jsonParseError ?? error) && (
                                <div style={{
                                    marginTop: '8px',
                                    padding: '6px 10px',
                                    backgroundColor: '#ff5252',
                                    color: '#fff',
                                    borderRadius: '3px',
                                    fontSize: '11px'
                                }}>
                                    {jsonParseError ?? error}
                                </div>
                            )}
                        </>
                    )}

                    {error && (
                        <div style={{
                            marginTop: '8px',
                            padding: '6px 10px',
                            backgroundColor: '#ff5252',
                            color: '#fff',
                            borderRadius: '3px',
                            fontSize: '11px'
                        }}>
                            {error}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ============================================================================
// Main BehaviorsPanel Component
// ============================================================================

const isDescendantOf = (node: BabylonNode, ancestor: BabylonNode): boolean => {
    let current: BabylonNode | null = node;
    while (current) {
        if (current === ancestor) return true;
        current = current.parent;
    }
    return false;
};

const BehaviorsPanel: React.FC = () => {
    // Store object reference - use selectedObject directly (getPath() for events, name for display)
    const [selectedObject, setSelectedObject] = useState<SceneObject | null>(null);
    const selectedObjectRef = useRef<SceneObject | null>(null);
    const [behaviors, setBehaviors] = useState<TraceBehavior[]>([]);
    const [jsonView, setJsonView] = useState(false);
    const [jsonText, setJsonText] = useState('');
    const [jsonDirty, setJsonDirty] = useState(false);
    const [jsonError, setJsonError] = useState<string | null>(null);
    const [active, setActive] = useState(true);
    const [editingScript, setEditingScript] = useState<ScriptBehavior | null>(null);
    const [renamingName, setRenamingName] = useState<string | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Get reference to the trace-viewer element
    const getViewerElement = (): TraceViewerElement | null => {
        return document.querySelector('trace-viewer');
    };

    // Keep ref in sync with state for use in event handlers
    useEffect(() => { selectedObjectRef.current = selectedObject; }, [selectedObject]);

    // Get the selected SceneObject - now just returns the stored reference
    const getSelectedSceneObject = useCallback(() => {
        return selectedObject;
    }, [selectedObject]);

    // Build full SceneObjectData using authored data (preserves $name refs)
    // with runtime transform values captured for non-ref fields.
    const getFullSceneData = useCallback((sceneObject: SceneObject): SceneData => {
        // Refresh scene data first — captureTransform() merges runtime values
        // into authored data while preserving $name property references.
        // SceneObject.captureTransform consults Properties.getAuthoredTransform
        // so it does not stomp authored data with a state/contribution-driven
        // live pose; safe to call unconditionally.
        sceneObject.refreshSceneData();
        // getSceneObjectData() returns a deep clone of the authored data
        return sceneObject.getSceneObjectData();
    }, []);

    // Load behaviors for a SceneObject
    const loadBehaviors = useCallback((sceneObject: SceneObject) => {
        // Show all runtime behaviors (no filtering)
        const allBehaviors = sceneObject.getBehaviors();
        setBehaviors(allBehaviors);

        // Clear rename mode on selection/refresh
        setRenamingName(null);

        // Load active state from the actual transform node (runtime truth)
        setActive(sceneObject.transform?.isEnabled() ?? sceneObject.active);

        // Update JSON view text with full SceneObject data (current runtime state)
        setJsonText(JSON.stringify(getFullSceneData(sceneObject), null, 2));
        setJsonDirty(false);
        setJsonError(null);

        // Exit script editor if it was open
        setEditingScript(null);

        console.log(`[Behaviors Panel] Loaded ${allBehaviors.length} behaviors for ${sceneObject.name}`);
    }, [getFullSceneData]);

    // Handle active state change
    const handleActiveChange = useCallback((value: boolean) => {
        const sceneObject = getSelectedSceneObject();
        if (sceneObject) {
            // Where it is seen: a selected keyframe, the showing page, a showing
            // state, or Base. The checkbox then shows what the object shows.
            void toggleActive(sceneObject, value).then((where) => {
                // The selection moved on meanwhile: the panel re-reads the new object.
                if (getSelectedSceneObject() !== sceneObject) return;
                // A keyframe applies a moment later; the poll picks it up.
                if (where === 'keyframe') return;
                setActive(sceneObject.transform?.isEnabled() ?? sceneObject.active);
                if (jsonView && !jsonDirty) {
                    setJsonText(JSON.stringify(getFullSceneData(sceneObject), null, 2));
                }
            });
        }
    }, [getSelectedSceneObject, jsonView, jsonDirty, getFullSceneData]);

    // Handle renaming the selected SceneObject
    const commitRename = useCallback((newName: string) => {
        setRenamingName(null);
        const sceneObject = getSelectedSceneObject();
        if (!sceneObject) return;

        const trimmed = newName.trim();
        if (!trimmed || trimmed === sceneObject.name) return;

        const viewer = getViewerElement();
        const traceScene = viewer?.traceScene;
        if (!traceScene) return;

        const oldName = sceneObject.name;

        const success = traceScene.renameSceneObject(sceneObject, trimmed);
        if (!success) return;

        if (!getUndoManager().isUndoRedoing) {
            // Ref-based commands survive renames automatically — no path rewrite needed.
            getUndoManager().push(new RenameCommand(sceneObject, oldName, trimmed));
        }

        // Refresh panel
        loadBehaviors(sceneObject);
    }, [getSelectedSceneObject, loadBehaviors]);

    // Handle adding a new behavior
    const handleAddBehavior = async (type: string) => {
        const sceneObject = getSelectedSceneObject();
        if (!sceneObject) return;
        // Adding a behavior to a descendant of a placement is an edit inside a
        // reference — copy the asset into this scene first (§2.3 "Editing").
        if (!await copyForInsideEdit(sceneObject)) return;

        try {
            const newBehavior = await sceneObject.addBehavior(type);

            console.log(`[Behaviors Panel] Added ${type} behavior`);

            // Record undo command
            if (!getUndoManager().isUndoRedoing) {
                getUndoManager().push(new AddBehaviorCommand(
                    sceneObject,
                    type,
                    newBehavior.getData(),
                ));
            }

            dispatchBehaviorChanged({
                objectPath: selectedObject!.getPath(),
                behaviorType: type,
                action: 'added'
            });

            if (selectedObject) {
                loadBehaviors(selectedObject);
            }

            // Scroll to bottom so the Add button stays visible
            requestAnimationFrame(() => {
                scrollContainerRef.current?.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' });
            });
        } catch (err) {
            console.error('[Behaviors Panel] Error adding behavior:', err);
        }
    };

    // Handle creating a new script behavior
    const handleNewScript = async () => {
        const sceneObject = getSelectedSceneObject();
        if (!sceneObject) return;

        const typeName = window.prompt('Enter a name for the new script (e.g., "Wobble"):');
        if (!typeName?.trim()) return;

        const name = typeName.trim();
        const assetPath = `./assets/scripts/${name}.ts`;
        const source = SCRIPT_TEMPLATE.replace(/\$\{typeName\}/g, name);

        // Register in TraceScene (stores blob, updates scriptsMap, compiles)
        sceneObject.traceScene.registerScript(name, assetPath, source);

        // Add the script behavior to the selected object
        await handleAddBehavior(name);
    };

    // Handle removing a behavior
    const handleRemoveBehavior = (behavior: TraceBehavior) => {
        const sceneObject = getSelectedSceneObject();
        if (!sceneObject) return;
        if (needsReferenceCopy(sceneObject)) {
            // Same rule as adding one, and the removal has to wait for the copy
            // — the behavior instance survives it (the node keeps its identity).
            void copyForInsideEdit(sceneObject).then(copied => {
                if (copied) handleRemoveBehavior(behavior);
            });
            return;
        }

        try {
            const behaviorType = behavior.name;
            const behaviorData = behavior.getData();
            const ctor = behavior.constructor as abstract new (...args: never) => TraceBehavior;
            sceneObject.removeBehavior(ctor);

            // Record undo command
            if (!getUndoManager().isUndoRedoing) {
                getUndoManager().push(new RemoveBehaviorCommand(
                    sceneObject,
                    behaviorType,
                    behaviorData,
                ));
            }

            console.log(`[Behaviors Panel] Removed ${behaviorType} behavior`);
            // Notify other panels
            dispatchBehaviorChanged({
                objectPath: sceneObject.getPath(),
                behaviorType,
                action: 'removed'
            });
            loadBehaviors(sceneObject);
        } catch (err) {
            console.error('[Behaviors Panel] Error removing behavior:', err);
        }
    };

    // Handle reload after commit
    // Handle JSON text changes
    const handleJsonChange = (text: string) => {
        setJsonText(text);
        setJsonDirty(true);
        setJsonError(null);
    };

    // Apply JSON changes (full SceneObjectData)
    const applyJsonChanges = async () => {
        const sceneObject = getSelectedSceneObject();
        if (!sceneObject) return;

        const viewer = getViewerElement();
        if (!viewer?.scene) return;

        try {
            const newData = JSON.parse(jsonText) as SceneData;

            // Validate structure
            if (typeof newData !== 'object' || newData === null) {
                setJsonError('Data must be an object');
                return;
            }

            if (typeof newData.name !== 'string') {
                setJsonError('Missing or invalid "name" property');
                return;
            }

            if (!Array.isArray(newData.behaviors)) {
                setJsonError('"behaviors" must be an array');
                return;
            }

            for (const behavior of newData.behaviors) {
                if (!behavior['@type']) {
                    setJsonError('Each behavior must have an "@type" property');
                    return;
                }
            }

            // Use setData to apply the full SceneObjectData and reload
            await sceneObject.setData(newData);

            // Update active state in UI
            setActive(newData.active !== false);

            console.log('[Behaviors Panel] Applied JSON changes');
            loadBehaviors(sceneObject);
            setJsonDirty(false);
            setJsonError(null);
        } catch (err) {
            setJsonError((err as Error).message);
        }
    };

    // Revert JSON changes
    const revertJsonChanges = () => {
        const sceneObject = getSelectedSceneObject();
        if (sceneObject) {
            // Get full SceneObject data with current runtime state
            setJsonText(JSON.stringify(getFullSceneData(sceneObject), null, 2));
            setJsonDirty(false);
            setJsonError(null);
        }
    };

    // Helper to find SceneObject from a Babylon node (uses array to handle duplicate names)
    const findSceneObjectForEntity = useCallback((entity: unknown): SceneObject | null => {
        const viewer = getViewerElement();
        if (!viewer?.traceScene) return null;

        // Use array-based lookup to handle duplicate names correctly
        const sceneObjects = viewer.traceScene.getSceneObjects();
        const transformToObject = new Map<unknown, SceneObject>();

        for (const sceneObject of sceneObjects) {
            if (sceneObject.transform) {
                transformToObject.set(sceneObject.transform, sceneObject);
            }
        }

        let current: BabylonNode | null = entity as BabylonNode;
        while (current) {
            const sceneObject = transformToObject.get(current);
            if (sceneObject !== undefined) return sceneObject;
            current = current.parent;
        }

        // Fallback: if walking up the parent chain didn't find a SceneObject transform,
        // iterate through all SceneObjects and check if any of them contain this entity
        // in their transform hierarchy. This handles the case where entity is an internal
        // node within a SceneObject's loaded GLB.
        if (entity && typeof entity === 'object' && 'parent' in entity) {
            for (const sceneObject of sceneObjects) {
                if (sceneObject.transform) {
                    // Check if entity is within this SceneObject's hierarchy
                    if (isDescendantOf(entity as BabylonNode, sceneObject.transform)) {
                        return sceneObject;
                    }
                }
            }
        }

        return null;
    }, []);

    // Listen for selection events to update panel UI state.
    // The SelectionBridgeService handles the bidirectional sync between
    // trace-viewer and the Inspector's selectionService (including gizmos).
    // This useEffect only updates local React state (selectedObject, behaviors).
    useEffect(() => {
        const selectionService = getSelectionService();

        const handleViewportSelection = (event: Event) => {
            const customEvent = event as CustomEvent<{ object: SceneObject | null }>;
            const sceneObject = customEvent.detail.object;

            if (sceneObject) {
                // If we're currently showing a Pages container, and the new selection
                // is a descendant of it, keep the panel on the Pages container.
                const current = selectedObjectRef.current;
                const isCurrentPages = current?.getBehavior(Pages) !== null;
                if (isCurrentPages && current && sceneObject !== current &&
                    isDescendantOf(sceneObject.transform, current.transform)) {
                    return;
                }

                setSelectedObject(sceneObject);
                loadBehaviors(sceneObject);
            } else if (customEvent.detail.object === null) {
                // Selecting nothing selects the DOCUMENT ROOT (SCENE_ROOT_SPEC
                // D8): the panel then shows the file's own data — its root
                // behaviors, its name, its pivot — instead of going blank.
                const fallback = documentRoot(getViewerElement()?.traceScene);
                setSelectedObject(fallback);
                if (fallback) loadBehaviors(fallback);
                else setBehaviors([]);
            }
        };

        const handleInspectorSelection = () => {
            if (!selectionService) return;

            const entity = selectionService.selectedEntity;
            if (entity) {
                const sceneObject = findSceneObjectForEntity(entity);
                if (sceneObject) {
                    setSelectedObject(sceneObject);
                    loadBehaviors(sceneObject);
                }
            }
        };

        window.addEventListener('trace-viewer:selection-changed', handleViewportSelection);

        let observer: { remove: () => void } | null = null;
        if (selectionService) {
            observer = selectionService.onSelectedEntityChanged.add(handleInspectorSelection);
            handleInspectorSelection();
        }

        return () => {
            window.removeEventListener('trace-viewer:selection-changed', handleViewportSelection);
            observer?.remove();
        };
    }, [loadBehaviors, findSceneObjectForEntity]);

    // Poll for active state changes (handles changes from Inspector or other sources)
    useEffect(() => {
        if (!selectedObject?.transform) return;

        const updateActiveFromScene = () => {
            const isEnabled = selectedObject.transform?.isEnabled() ?? true;
            setActive(prev => prev !== isEnabled ? isEnabled : prev);
        };

        // Poll at 10fps to stay synced with scene
        const intervalId = setInterval(updateActiveFromScene, 100);

        return () => clearInterval(intervalId);
    }, [selectedObject]);

    // Track if panel needs refresh (set when mouse enters after being away)
    const lastRefreshTime = useRef<number>(0);
    const REFRESH_DEBOUNCE_MS = 500; // Don't refresh more than once per 500ms

    // Re-load behaviors when panel gains focus or mouse enters (handles external changes from other panels)
    const refreshIfNeeded = useCallback(() => {
        if (!selectedObject) return;

        const now = Date.now();
        if (now - lastRefreshTime.current > REFRESH_DEBOUNCE_MS) {
            lastRefreshTime.current = now;
            loadBehaviors(selectedObject);
        }
    }, [selectedObject, loadBehaviors]);

    useEffect(() => {
        const handleFocus = () => {
            refreshIfNeeded();
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshIfNeeded();
            }
        };

        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [refreshIfNeeded]);

    // F2 hotkey to rename the selected object
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'F2') return;
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
            if (!selectedObjectRef.current) return;
            e.preventDefault();
            setRenamingName(selectedObjectRef.current.name);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Listen for editor behavior-changed events (fired by other panels when behaviors are modified)
    // Use a ref flag to skip self-dispatched events (we already updated our own state in liveCommit)
    const selfDispatchingRef = useRef(false);
    useEffect(() => {
        const handleBehaviorChanged = (event: Event) => {
            // Skip events we dispatched ourselves
            if (selfDispatchingRef.current) return;

            const customEvent = event as CustomEvent<BehaviorChangedDetail>;
            const changedObjectPath = customEvent.detail.objectPath;

            // Only refresh if the changed object is the one we're viewing (compare by path)
            if (selectedObject && changedObjectPath === selectedObject.getPath()) {
                console.log('[Behaviors Panel] Behavior changed externally, refreshing...');
                loadBehaviors(selectedObject);
            }
        };

        window.addEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, handleBehaviorChanged);

        return () => {
            window.removeEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, handleBehaviorChanged);
        };
    }, [selectedObject, loadBehaviors]);

    // Refresh the panel when the keyframe overlay changes which keyframe is
    // selected. Applying a keyframe mutates runtime state on the selected object
    // (visibility/active, transform, the keyframe's stored property values), so
    // if the object we're viewing sits under the container whose selection
    // changed, re-read it. We listen for a dedicated event rather than
    // BEHAVIOR_CHANGED so this refresh can't be misread as a Properties commit by
    // the keyframe capture listener.
    //
    // One reload, on selection — each Properties section reseeds its own
    // localData from the keyframe's STORED values (overlayKeyframeValues), which
    // are stable for the whole time the keyframe is selected, so there's nothing
    // to re-poll as the viewport transition settles. A settle-timer reload here
    // used to fire mid-edit and clobber the field the user was typing into —
    // removed deliberately.
    useEffect(() => {
        if (!selectedObject) return;
        const handleSelectionChanged = (event: Event) => {
            const detail = (event as CustomEvent<KeyframeSelectionChangedDetail>).detail;
            // Refresh when the viewed object is the container itself or a
            // descendant of it (its applied pose/values depend on the keyframe).
            const containerPath = detail.containerPath;
            const objectPath = selectedObject.getPath();
            const isContainerOrDescendant =
                objectPath === containerPath || objectPath.startsWith(`${containerPath}/`);
            if (!isContainerOrDescendant) return;
            loadBehaviors(selectedObject);
        };
        window.addEventListener(EDITOR_EVENTS.KEYFRAME_SELECTION_CHANGED, handleSelectionChanged);
        return () => {
            window.removeEventListener(EDITOR_EVENTS.KEYFRAME_SELECTION_CHANGED, handleSelectionChanged);
        };
    }, [selectedObject, loadBehaviors]);

    // Get existing behavior types for the add dropdown
    const existingTypes = behaviors.map(b => b.getData()['@type']);

    // Get script types from ScriptRegistry (via scriptsMap keys)
    const scriptTypes = selectedObject
        ? Object.keys(selectedObject.traceScene.scriptsMap ?? {})
        : [];

    return (
        <div
            style={{
                padding: '12px',
                color: '#ffffff',
                backgroundColor: '#252525',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: 'system-ui, -apple-system, sans-serif',
            }}
        >
            {!selectedObject ? (
                <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '13px',
                }}>
                    Select an object to view its behaviors
                </div>
            ) : (
                <>
                    <div style={{
                        marginBottom: '12px',
                        fontSize: '13px',
                        color: '#888',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}>
                        {editingScript ? (
                            /* Script editor header — back button + script name */
                            <>
                                <button
                                    onClick={() => setEditingScript(null)}
                                    style={{
                                        padding: '4px 8px',
                                        backgroundColor: '#383838',
                                        border: 'none',
                                        borderRadius: '3px',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                    }}
                                    title="Back to behaviors"
                                >
                                    ←
                                </button>
                                <strong style={{ color: '#9cb', flex: 1 }}>{editingScript.getData()['@type']}</strong>
                            </>
                        ) : (
                            /* Normal header — JSON toggle + object name + active checkbox */
                            <>
                                <button
                                    onClick={() => {
                                        const entering = !jsonView;
                                        if (entering && selectedObject) {
                                            // Snapshot current runtime transform so JSON isn't stale
                                            setJsonText(JSON.stringify(getFullSceneData(selectedObject), null, 2));
                                            setJsonDirty(false);
                                            setJsonError(null);
                                        }
                                        setJsonView(entering);
                                    }}
                                    style={{
                                        padding: '4px 8px',
                                        backgroundColor: jsonView ? '#0078d4' : '#383838',
                                        border: 'none',
                                        borderRadius: '3px',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                    }}
                                    title={jsonView ? 'Switch to Inspector view' : 'Switch to JSON view'}
                                >
                                    {'{ }'}
                                </button>
                                {renamingName !== null ? (
                                    <input
                                        type="text"
                                        value={renamingName}
                                        onChange={(e) => setRenamingName(e.target.value)}
                                        onBlur={() => commitRename(renamingName)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') commitRename(renamingName);
                                            if (e.key === 'Escape') setRenamingName(null);
                                        }}
                                        onFocus={(e) => e.target.select()}
                                        autoFocus
                                        style={{
                                            flex: 1,
                                            background: 'rgba(0,0,0,0.4)',
                                            border: '1px solid #0078d4',
                                            borderRadius: '3px',
                                            color: '#fff',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            padding: '1px 4px',
                                            outline: 'none',
                                            minWidth: 0,
                                        }}
                                    />
                                ) : (
                                    <strong
                                        onDoubleClick={() => setRenamingName(selectedObject.name)}
                                        style={{ color: '#fff', flex: 1, cursor: 'text' }}
                                        title="Double-click to rename"
                                    >
                                        {selectedObject.name}
                                    </strong>
                                )}
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                    <span style={{ fontSize: '11px', color: '#888' }}>Active</span>
                                    <input
                                        type="checkbox"
                                        checked={active}
                                        onChange={(e) => handleActiveChange(e.target.checked)}
                                        style={{ width: '14px', height: '14px', cursor: 'pointer', margin: 0 }}
                                    />
                                </label>
                            </>
                        )}
                    </div>

                    <div ref={scrollContainerRef} style={{ flex: 1, minHeight: 0, overflow: 'auto', display: (jsonView || editingScript) ? 'flex' : 'block', flexDirection: 'column' }}>
                        {editingScript ? (
                            /* Script Editor View */
                            <ScriptEditorSection
                                behavior={editingScript}
                            />
                        ) : jsonView ? (
                            /* JSON View */
                            <>
                                <JsonEditor
                                    value={jsonText}
                                    onChange={handleJsonChange}
                                    error={jsonError}
                                />
                                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                    <button
                                        onClick={applyJsonChanges}
                                        disabled={!jsonDirty}
                                        style={{
                                            flex: 1,
                                            padding: '8px 16px',
                                            backgroundColor: jsonDirty ? '#0078d4' : '#444',
                                            border: 'none',
                                            borderRadius: '4px',
                                            color: '#fff',
                                            cursor: jsonDirty ? 'pointer' : 'not-allowed',
                                            fontSize: '13px',
                                        }}
                                    >
                                        Apply Changes
                                    </button>
                                    <button
                                        onClick={revertJsonChanges}
                                        disabled={!jsonDirty}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: jsonDirty ? '#555' : '#444',
                                            border: 'none',
                                            borderRadius: '4px',
                                            color: '#fff',
                                            cursor: jsonDirty ? 'pointer' : 'not-allowed',
                                            fontSize: '13px',
                                        }}
                                    >
                                        Revert
                                    </button>
                                </div>
                            </>
                        ) : (
                            /* Inspector View */
                            <>
                                {/* Transform Section */}
                                <TransformSection sceneObject={getSelectedSceneObject()} />

                                {behaviors.length > 0 ? (
                                    behaviors.map((behavior, index) => (
                                        <BehaviorSection
                                            key={`${behavior.getData()['@type']}-${index}`}
                                            behavior={behavior}
                                            onRemove={() => handleRemoveBehavior(behavior)}
                                            onSaved={(behaviorType) => {
                                                if (selectedObject) {
                                                    // try/finally: if a BEHAVIOR_CHANGED listener
                                                    // throws synchronously, we still clear the flag
                                                    // so future dispatches aren't permanently
                                                    // suppressed. See the selfCommittingRef comment
                                                    // in liveCommit() for the full rationale.
                                                    selfDispatchingRef.current = true;
                                                    try {
                                                        dispatchBehaviorChanged({
                                                            objectPath: selectedObject.getPath(),
                                                            behaviorType,
                                                            action: 'updated'
                                                        });
                                                    } finally {
                                                        selfDispatchingRef.current = false;
                                                    }
                                                }
                                            }}
                                            onEditScript={setEditingScript}
                                        />
                                    ))
                                ) : ''}

                                <AddBehaviorButton
                                    onAdd={handleAddBehavior}
                                    existingTypes={existingTypes}
                                    scriptTypes={scriptTypes}
                                    onNewScript={handleNewScript}
                                />
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

// ============================================================================
// Icon and Service Definition
// ============================================================================

const BehaviorsIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M3 3.5A1.5 1.5 0 0 1 4.5 2h7A1.5 1.5 0 0 1 13 3.5v9a1.5 1.5 0 0 1-1.5 1.5h-7A1.5 1.5 0 0 1 3 12.5v-9zM4.5 3a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h7a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5h-7z"/>
        <path d="M5 5.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5z"/>
    </svg>
);

const ShowPropertiesOnSelectionSetting: SettingDescriptor<boolean> = {
    key: 'ShowPropertiesOnSelection',
    defaultValue: true,
};

export const BehaviorsPanelServiceDefinition: ServiceDefinition<[], [IShellService, ISettingsStore]> = {
    friendlyName: 'Behaviors Panel',
    consumes: [ShellServiceIdentity, SettingsStoreIdentity],

    factory: (shellService: IShellService, settingsStore: ISettingsStore) => {
        console.log('[Behaviors Panel] Initializing...');
        settingsStore.writeSetting(ShowPropertiesOnSelectionSetting, false);

        const paneDefinition: SidePaneDefinition = {
            key: 'behaviors-panel',
            icon: BehaviorsIcon,
            content: BehaviorsPanel,
            horizontalLocation: 'right',
            verticalLocation: 'top',
            order: -2,
            title: 'Behaviors'
        };

        const disposable = shellService.addSidePane(paneDefinition);
        console.log('[Behaviors Panel] Ready');

        return {
            dispose: () => {
                disposable.dispose();
            }
        };
    }
};
